/**
 * Trigger a browser download for an in-memory blob.
 *
 * The object URL is revoked immediately after the synthetic click; keeping it alive
 * leaks the blob for the lifetime of the document, which matters for print-resolution
 * PDFs that run to tens of megabytes.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Read the filename a server chose from its Content-Disposition header.
 *
 * Preferred over naming the file client-side so the server stays the single source of
 * truth for what a download is called. Returns '' when the header is absent or unparsable.
 */
export function filenameFromContentDisposition(header) {
  if (!header) return '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch (_) {
      // Fall through to the plain filename below.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : '';
}

/**
 * Pull a readable message out of a failed request made with `responseType: 'blob'`.
 *
 * Axios hands back the error body as a Blob in that mode, so the usual
 * `err.response.data.detail` is undefined and a naive handler shows nothing useful.
 */
export async function errorDetailFromResponse(err, fallback) {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.detail || parsed.hunt_boundary_id?.[0] || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return data?.detail || err?.message || fallback;
}
